import { filter } from 'rxjs/operators';
import { Component, ViewChild, AfterViewInit, OnDestroy, TemplateRef } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from './services/common/auth.service';
import { AlertService } from './services/common/alert.service';
import { ModalService } from './services/common/modal.service';
import { NotificationChannelService } from './services/channels/notification-channel.service';
import { NotyService } from './services/common/noty.service';
import { UserService } from './services/users/user.service';

import { AlertDialogComponent } from './components/common/alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from './components/common/confirm-dialog/confirm-dialog.component';
import { ModalConfirmComponent } from './components/common/modal-confirm/modal-confirm.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  @ViewChild('alertDialog', { static: true }) alertDialogComponent: AlertDialogComponent;
  @ViewChild('confirmDialog', { static: true }) confirmDialogComponent: ConfirmDialogComponent;
  @ViewChild('confirmModal', { static: true }) confirmModalComponent: ModalConfirmComponent;
  @ViewChild('lateReasonModal', { static: true }) lateReasonModal: TemplateRef<any>;

  showNavbar: boolean = true;

  signInChecked: boolean = false;

  signingIn: boolean = false;

  signInParams: any = {};

  userSignedIn: boolean = false;

  lateNotice: any;

  lateReason: string = '';

  savingLateReason: boolean = false;

  private lateReasonModalRef: NgbModalRef | undefined;

  // Kept only in memory so a restored JWT after a reboot presents a fresh
  // browser session to the server.
  private attendanceSessionId: string = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private attendanceHeartbeatTimer: any;
  private readonly attendanceHeartbeatIntervalMs: number = 60 * 1000;
  private readonly attendanceHeartbeatLeaseMs: number = 90 * 1000;

  constructor(private authService: AuthService, private alertService: AlertService, private modalService: ModalService, private notificationChannelService: NotificationChannelService, private notyService: NotyService, private router: Router, private route: ActivatedRoute, private userService: UserService, private ngbModal: NgbModal) {
    this.checkLogin();
  }

  ngAfterViewInit() {
    this.modalService.alertDialogComponent = this.alertDialogComponent;
    this.modalService.confirmDialogComponent = this.confirmDialogComponent;
    this.modalService.confirmModal = this.confirmModalComponent;
    if (this.router.events) {
      this.router.events.subscribe((val) => {
        this.alertService.alert_status_clear();
        this.alertService.alert_status_clear_center();
        this.alertService.alert_status_clear_overlay();
        this.modalService.confirmModal.closeModal();
      });
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
        this.navbarCheck();
      });
    }
  }

  ngOnDestroy() {
    if (this.attendanceHeartbeatTimer) {
      window.clearInterval(this.attendanceHeartbeatTimer);
      this.attendanceHeartbeatTimer = undefined;
    }
    window.removeEventListener('beforeunload', this.releaseAttendanceHeartbeatLease);
    this.releaseAttendanceHeartbeatLease();
  }

  checkLogin() {
    const urlParams = new URLSearchParams(window.location.search);

    // First check for auth_token in URL (passed from Devise login)
    const authToken = urlParams.get('auth_token');
    if (authToken) {
      // Store token and clean up URL
      window.localStorage.setItem('token', authToken);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check for redirect_to with valid token - redirect immediately before any API calls
    const redirectTo = urlParams.get('redirect_to');
    const token = window.localStorage.getItem('token');
    if (redirectTo && token && token !== 'null' && token !== 'undefined') {
      const separator = redirectTo.includes('?') ? '&' : '?';
      window.location.href = `${redirectTo}${separator}token=${encodeURIComponent(token)}`;
      return;
    }

    if (this.signInChecked) {
      this.authService.getCurrentUserGroups(true).then((response_json) => {
        this.notificationChannelService.connectForBrowserNotifications(String(this.authService.getUser().id));
        this.notificationChannelService.connectForWebPageNotifications(String(this.authService.getUser().id));
        this.handleRedirectOrNavigate();
      });
    }

    // Check for existing token in localStorage (auth_token may have just been stored above)
    if (token && token !== 'null' && token !== 'undefined') {
      this.proceedWithLogin();
      return;
    }

    // No token found - a Rails page may still have a valid Devise session cookie.
    this.userService
      .checkSession()
      .then((response_json) => {
        if (response_json.success && response_json.token) {
          window.localStorage.setItem('token', response_json.token);
          this.proceedWithLogin();
        } else {
          this.signInChecked = true;
          this.userSignedIn = false;
        }
      })
      .catch(() => {
        this.signInChecked = true;
        this.userSignedIn = false;
      });
  }

  private proceedWithLogin() {
    this.userService
      .getUserShowData()
      .then(() => this.authService.getCurrentUserGroups(true).catch(() => undefined))
      .then(() => {
        this.signInChecked = true;
        this.userSignedIn = true;
        if (this.authService.getUser().isStaff()) this.startAttendanceHeartbeat();
        // Check for redirect_to parameter after successful auth check
        this.handleRedirectToParam();
      })
      .catch(() => {
        this.signInChecked = true;
        this.userSignedIn = false;
      });
  }

  private startAttendanceHeartbeat() {
    if (this.attendanceHeartbeatTimer) return;

    const send = () => {
      if (!this.userSignedIn || !this.claimAttendanceHeartbeatLease()) return;
      this.userService.heartbeat(this.attendanceSessionId).catch(() => {
        // A missed heartbeat must not interrupt the application UI.
      });
    };

    window.addEventListener('beforeunload', this.releaseAttendanceHeartbeatLease);
    send();
    this.attendanceHeartbeatTimer = window.setInterval(send, this.attendanceHeartbeatIntervalMs);
  }

  private attendanceHeartbeatLeaseKey(): string {
    return `v7-attendance-heartbeat-leader:${this.authService.getUser().id}`;
  }

  // Only one tab in a browser should send attendance heartbeats. The short
  // lease lets another tab take over if the active tab closes or is suspended.
  private claimAttendanceHeartbeatLease(now: number = Date.now()): boolean {
    try {
      const key = this.attendanceHeartbeatLeaseKey();
      const current = JSON.parse(window.localStorage.getItem(key) || 'null');
      if (current && current.sessionId !== this.attendanceSessionId && Number(current.expiresAt) > now) {
        return false;
      }

      const lease = {
        sessionId: this.attendanceSessionId,
        expiresAt: now + this.attendanceHeartbeatLeaseMs,
      };
      window.localStorage.setItem(key, JSON.stringify(lease));
      const confirmed = JSON.parse(window.localStorage.getItem(key) || 'null');
      return confirmed && confirmed.sessionId === this.attendanceSessionId;
    } catch (_error) {
      // Browsers can disable localStorage. Preserve attendance recording in
      // that case, even though tab coordination is unavailable.
      return true;
    }
  }

  private releaseAttendanceHeartbeatLease = () => {
    try {
      const key = this.attendanceHeartbeatLeaseKey();
      const current = JSON.parse(window.localStorage.getItem(key) || 'null');
      if (current && current.sessionId === this.attendanceSessionId) {
        window.localStorage.removeItem(key);
      }
    } catch (_error) {
      // Nothing to release when browser storage is unavailable.
    }
  };

  /**
   * Handle redirect_to query parameter for Rails-only routes
   * If present and user has valid token, redirect to that URL with token appended
   */
  handleRedirectToParam() {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get('redirect_to');
    if (redirectTo) {
      const token = window.localStorage.getItem('token');
      if (token && token !== 'null' && token !== 'undefined') {
        // Redirect to Rails route with token appended (URL-encoded)
        const separator = redirectTo.includes('?') ? '&' : '?';
        window.location.href = `${redirectTo}${separator}token=${encodeURIComponent(token)}`;
      }
    }
  }

  /**
   * Handle redirect after login - check for redirect_to param or navigate to root
   */
  handleRedirectOrNavigate() {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get('redirect_to');
    if (redirectTo) {
      const token = window.localStorage.getItem('token');
      if (token && token !== 'null' && token !== 'undefined') {
        const separator = redirectTo.includes('?') ? '&' : '?';
        window.location.href = `${redirectTo}${separator}token=${encodeURIComponent(token)}`;
        return;
      }
    }
    this.router.navigateByUrl('/');
  }

  navbarCheck(): boolean {
    this.showNavbar = true;
    return this.showNavbar;
  }

  signIn() {
    this.signingIn = true;
    this.userService
      .signIn(this.signInParams)
      .then((response_json) => {
        this.signingIn = false;
        if (response_json.success && response_json.token) {
          window.localStorage.setItem('token', response_json.token);
          // Force reload of user groups after login to populate navbar
          this.authService.getCurrentUserGroups(true).then(() => {
            this.checkLogin();
            if (response_json.attendance_notice && response_json.attendance_notice.reason_required) {
              this.lateNotice = response_json.attendance_notice;
              setTimeout(() => this.openLateReasonDialog(), 250);
            }
          });
        } else {
          this.notyService.notify('Invalid username or password', 'error', 5000);
        }
      })
      .catch((error) => {
        this.notyService.notify('Invalid username or password', 'error', 5000);
        this.signingIn = false;
        this.signInChecked = true;
        this.userSignedIn = false;
      });
  }

  openLateReasonDialog() {
    if (!this.lateNotice || !this.lateReasonModal || this.lateReasonModalRef) return;

    this.lateReason = '';
    this.savingLateReason = false;
    this.lateReasonModalRef = this.ngbModal.open(this.lateReasonModal, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
    this.lateReasonModalRef.result.then(() => {
      this.resetLateReasonDialog();
    }, () => {
      this.resetLateReasonDialog();
    });
  }

  private resetLateReasonDialog() {
    if (!this.lateReasonModalRef) return;

    this.lateReasonModalRef = undefined;
    this.lateNotice = undefined;
    this.lateReason = '';
  }

  saveLateReason(modal: any) {
    const reason = this.lateReason.toString().trim();
    if (!reason || this.savingLateReason) return;

    this.savingLateReason = true;
    this.userService.saveLateReason(reason).then(() => {
      this.savingLateReason = false;
      modal.close('saved');
      this.notyService.notify('Late arrival explanation saved.', 'success');
    }).catch(() => {
      this.savingLateReason = false;
      this.notyService.notify('Unable to save the late arrival explanation.', 'error');
    });
  }

  dismissLateReason(modal: any) {
    modal.dismiss('not_now');
  }
}
